import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCopy,
  faArrowRight,
  faBox,
  faTags,
  faTruck,
  faImage,
  faUndo,
  faCheckCircle,
  faExclamationTriangle,
  faSpinner,
  faEye,
  faEyeSlash,
  faLayerGroup,
  faSearch,
  faCheckSquare,
  faSquare
} from '@fortawesome/free-solid-svg-icons';
import api from '../../services/api';
import { useDashboardAuth } from '../../context/DashboardAuthContext';
import './StockDuplication.css';

const isDev = process.env.NODE_ENV === 'development';
const imageBaseURL = isDev ? '' : 'https://www.lonnii.com';

const StockDuplication = () => {
  const { isSuperAdmin } = useDashboardAuth();
  const canManage = isSuperAdmin();

  // State
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sourceGroupId, setSourceGroupId] = useState('');
  const [targetGroupId, setTargetGroupId] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [result, setResult] = useState(null);
  const [searchSource, setSearchSource] = useState('');
  const [searchTarget, setSearchTarget] = useState('');

  // Options
  const [includeCategories, setIncludeCategories] = useState(true);
  const [includeSuppliers, setIncludeSuppliers] = useState(true);
  const [includeImages, setIncludeImages] = useState(true);
  const [resetQuantities, setResetQuantities] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [selectAll, setSelectAll] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');  
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Fetch groups
  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/dashboard-admin/stock-duplication/groups');
      if (response.data.success) {
        setGroups(response.data.groups);
      }
    } catch (err) {
      console.error('Error fetching groups:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Preview source stock
  const handlePreview = async () => {
    if (!sourceGroupId) return;
    try {
      setPreviewLoading(true);
      setPreview(null);
      setResult(null);
      const response = await api.get(`/dashboard-admin/stock-duplication/preview/${sourceGroupId}`);
      if (response.data.success) {
        setPreview(response.data);
        // Select all products by default
        const allIds = new Set(response.data.products.map(p => p.id));
        setSelectedProducts(allIds);
        setSelectAll(true);
      }
    } catch (err) {
      console.error('Error previewing stock:', err);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Toggle product selection
  const toggleProduct = (productId) => {
    const newSet = new Set(selectedProducts);
    if (newSet.has(productId)) {
      newSet.delete(productId);
    } else {
      newSet.add(productId);
    }
    setSelectedProducts(newSet);
    setSelectAll(newSet.size === preview?.products?.length);
  };

  // Toggle all
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedProducts(new Set());
      setSelectAll(false);
    } else {
      const allIds = new Set(preview?.products?.map(p => p.id) || []);
      setSelectedProducts(allIds);
      setSelectAll(true);
    }
  };

  // Execute duplication
  const handleDuplicate = async () => {
    if (!sourceGroupId || !targetGroupId) return;
    if (selectedProducts.size === 0) return;
    if (!adminPassword) {
      setPasswordError('Veuillez entrer votre mot de passe');
      return;
    }

    setShowPasswordModal(false);

    try {
      setDuplicating(true);
      setResult(null);
      setPasswordError('');
      // The password is checked by the dashboard login service, which returns a
      // short-lived token for this one action; the Lonnii server only sees the token.
      const confirmation = await api.post('/dashboard-admin/confirm-password', {
        password: adminPassword,
        purpose: 'stock-duplication',
      });
      const response = await api.post('/dashboard-admin/stock-duplication/duplicate', {
        sourceGroupId,
        targetGroupId,
        confirmToken: confirmation.data.confirmToken,
        options: {
          includeCategories,
          includeSuppliers,
          includeImages,
          resetQuantities,
          selectedProductIds: selectAll ? null : Array.from(selectedProducts)
        }
      });

      if (response.data.success) {
        setResult(response.data);
        setAdminPassword('');
        fetchGroups(); // Refresh counts
      }
    } catch (err) {
      console.error('Duplication error:', err);
      const errMsg = err.response?.data?.error || 'Erreur lors de la duplication';
      if (err.response?.status === 403) {
        setPasswordError(errMsg);
        setShowPasswordModal(true);
      } else {
        setResult({ 
          success: false, 
          message: errMsg
        });
      }
      setAdminPassword('');
    } finally {
      setDuplicating(false);
    }
  };

  // Open password modal
  const promptPassword = () => {
    setAdminPassword('');
    setPasswordError('');
    setShowPasswordModal(true);
  };

  // Filter groups
  const filteredSourceGroups = groups.filter(g => 
    g.nom.toLowerCase().includes(searchSource.toLowerCase()) && g.id !== targetGroupId
  );
  const filteredTargetGroups = groups.filter(g => 
    g.nom.toLowerCase().includes(searchTarget.toLowerCase()) && g.id !== sourceGroupId
  );

  const sourceGroup = groups.find(g => g.id === sourceGroupId);
  const targetGroup = groups.find(g => g.id === targetGroupId);

  if (!canManage) {
    return (
      <div className="stock-duplication">
        <div className="access-denied">
          <h2>🚫 Accès Refusé</h2>
          <p>Vous n'avez pas la permission d'accéder à cette fonctionnalité.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="stock-duplication">
      <div className="page-header">
        <div className="header-content">
          <h1>
            <FontAwesomeIcon icon={faCopy} className="header-icon" />
            Duplication de Stock
          </h1>
          <p className="header-desc">
            Dupliquez l'intégralité du stock (produits, prix, catégories, fournisseurs, images) d'une Gestion vers une autre
          </p>
        </div>
      </div>

      {/* Group Selection */}
      <div className="selection-section">
        <div className="selection-grid">
          {/* Source */}
          <div className="selection-card source">
            <h3><FontAwesomeIcon icon={faLayerGroup} /> Source (copier depuis)</h3>
            <div className="search-box">
              <FontAwesomeIcon icon={faSearch} />
              <input 
                type="text" 
                placeholder="Rechercher un groupe..."
                value={searchSource}
                onChange={(e) => setSearchSource(e.target.value)}
              />
            </div>
            <div className="group-list">
              {loading ? (
                <div className="loading"><FontAwesomeIcon icon={faSpinner} spin /> Chargement...</div>
              ) : filteredSourceGroups.length === 0 ? (
                <div className="empty">Aucun groupe trouvé</div>
              ) : (
                filteredSourceGroups.map(group => (
                  <div 
                    key={group.id}
                    className={`group-item ${sourceGroupId === group.id ? 'selected' : ''}`}
                    onClick={() => { setSourceGroupId(group.id); setPreview(null); setResult(null); }}
                  >
                    <div className="group-name">{group.nom}</div>
                    <div className="group-stats">
                      <span><FontAwesomeIcon icon={faBox} /> {group.product_count}</span>
                      <span><FontAwesomeIcon icon={faTags} /> {group.category_count}</span>
                      <span><FontAwesomeIcon icon={faTruck} /> {group.supplier_count}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Arrow */}
          <div className="arrow-divider">
            <FontAwesomeIcon icon={faArrowRight} />
          </div>

          {/* Target */}
          <div className="selection-card target">
            <h3><FontAwesomeIcon icon={faLayerGroup} /> Destination (copier vers)</h3>
            <div className="search-box">
              <FontAwesomeIcon icon={faSearch} />
              <input 
                type="text" 
                placeholder="Rechercher un groupe..."
                value={searchTarget}
                onChange={(e) => setSearchTarget(e.target.value)}
              />
            </div>
            <div className="group-list">
              {loading ? (
                <div className="loading"><FontAwesomeIcon icon={faSpinner} spin /> Chargement...</div>
              ) : filteredTargetGroups.length === 0 ? (
                <div className="empty">Aucun groupe trouvé</div>
              ) : (
                filteredTargetGroups.map(group => (
                  <div 
                    key={group.id}
                    className={`group-item ${targetGroupId === group.id ? 'selected' : ''}`}
                    onClick={() => { setTargetGroupId(group.id); setResult(null); }}
                  >
                    <div className="group-name">{group.nom}</div>
                    <div className="group-stats">
                      <span><FontAwesomeIcon icon={faBox} /> {group.product_count}</span>
                      <span><FontAwesomeIcon icon={faTags} /> {group.category_count}</span>
                      <span><FontAwesomeIcon icon={faTruck} /> {group.supplier_count}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Selected Summary */}
        {(sourceGroup || targetGroup) && (
          <div className="selection-summary">
            <div className="summary-item">
              <strong>Source:</strong> {sourceGroup?.nom || '—'}
              {sourceGroup && <span className="badge">{sourceGroup.product_count} produits</span>}
            </div>
            <FontAwesomeIcon icon={faArrowRight} className="summary-arrow" />
            <div className="summary-item">
              <strong>Destination:</strong> {targetGroup?.nom || '—'}
              {targetGroup && <span className="badge">{targetGroup.product_count} produits</span>}
            </div>
          </div>
        )}
      </div>

      {/* Options */}
      <div className="options-section">
        <h3>Options de duplication</h3>
        <div className="options-grid">
          <label className="option-toggle">
            <input type="checkbox" checked={includeCategories} onChange={(e) => setIncludeCategories(e.target.checked)} />
            <FontAwesomeIcon icon={faTags} />
            <span>Copier les catégories</span>
          </label>
          <label className="option-toggle">
            <input type="checkbox" checked={includeSuppliers} onChange={(e) => setIncludeSuppliers(e.target.checked)} />
            <FontAwesomeIcon icon={faTruck} />
            <span>Copier les fournisseurs</span>
          </label>
          <label className="option-toggle">
            <input type="checkbox" checked={includeImages} onChange={(e) => setIncludeImages(e.target.checked)} />
            <FontAwesomeIcon icon={faImage} />
            <span>Copier les images</span>
          </label>
          <label className="option-toggle">
            <input type="checkbox" checked={resetQuantities} onChange={(e) => setResetQuantities(e.target.checked)} />
            <FontAwesomeIcon icon={faUndo} />
            <span>Remettre quantités à 0</span>
          </label>
        </div>
      </div>

      {/* Preview Button */}
      {sourceGroupId && (
        <div className="preview-action">
          <button 
            className="btn-preview" 
            onClick={handlePreview} 
            disabled={previewLoading}
          >
            {previewLoading ? (
              <><FontAwesomeIcon icon={faSpinner} spin /> Chargement...</>
            ) : (
              <><FontAwesomeIcon icon={faEye} /> Prévisualiser le stock source</>
            )}
          </button>
        </div>
      )}

      {/* Preview Results */}
      {preview && (
        <div className="preview-section">
          <div className="preview-header">
            <h3>Stock de "{sourceGroup?.nom}" — {preview.products.length} produit(s)</h3>
            <label className="select-all" onClick={toggleSelectAll}>
              <FontAwesomeIcon icon={selectAll ? faCheckSquare : faSquare} />
              {selectAll ? 'Désélectionner tout' : 'Sélectionner tout'}
            </label>
          </div>

          {preview.products.length === 0 ? (
            <div className="empty-preview">Aucun produit dans ce stock</div>
          ) : (
            <div className="products-table-wrapper">
              <table className="products-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Produit</th>
                    <th>Catégorie</th>
                    <th>Prix Achat</th>
                    <th>Prix Vente</th>
                    <th>Prix Détail</th>
                    <th>Marge</th>
                    <th>Qté</th>
                    <th>Unité</th>
                    <th>Attributs</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.products.map(product => (
                    <tr 
                      key={product.id} 
                      className={selectedProducts.has(product.id) ? 'selected' : 'unselected'}
                      onClick={() => toggleProduct(product.id)}
                    >
                      <td>
                        <FontAwesomeIcon 
                          icon={selectedProducts.has(product.id) ? faCheckSquare : faSquare} 
                          className={selectedProducts.has(product.id) ? 'check-on' : 'check-off'}
                        />
                      </td>
                      <td>
                        <div className="product-cell">
                          {product.image_path && (
                            <img 
                              src={`${imageBaseURL}${product.image_path}`} 
                              alt="" 
                              className="product-thumb" 
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <span>{product.name}</span>
                        </div>
                      </td>
                      <td>{product.category_name || '—'}</td>
                      <td>{product.cost_price ? `${new Intl.NumberFormat('fr-FR').format(Number(product.cost_price))} FCFA` : '—'}</td>
                      <td>{product.selling_price ? `${new Intl.NumberFormat('fr-FR').format(Number(product.selling_price))} FCFA` : '—'}</td>
                      <td>{product.detail_unit_price ? `${new Intl.NumberFormat('fr-FR').format(Number(product.detail_unit_price))} FCFA` : '—'}</td>
                      <td>{product.markup_percentage ? `${Number(product.markup_percentage).toFixed(2)}%` : '—'}</td>
                      <td>{product.quantity ?? 0}</td>
                      <td>{product.base_unit || '—'}{product.sell_unit && product.sell_unit !== product.base_unit ? ` → ${product.sell_unit}` : ''}</td>
                      <td>
                        <div className="attr-badges">
                          {product.vente_libre && <span className="attr-badge libre">Vente libre</span>}
                          {product.prix_fixe && <span className="attr-badge fixe">Prix fixe</span>}
                          {product.allow_mixed_sales && <span className="attr-badge mixte">Vente mixte</span>}
                          {!product.vente_libre && !product.prix_fixe && !product.allow_mixed_sales && '—'}
                        </div>
                      </td>
                      <td><span className={`type-badge ${product.stock_type || 'marchandise'}`}>{product.stock_type || 'marchandise'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Summary bar */}
          <div className="preview-summary">
            <span>{selectedProducts.size} / {preview.products.length} produit(s) sélectionné(s)</span>
            {includeCategories && <span><FontAwesomeIcon icon={faTags} /> {preview.categories.length} catégories</span>}
            {includeSuppliers && <span><FontAwesomeIcon icon={faTruck} /> {preview.suppliers.length} fournisseurs</span>}
          </div>
        </div>
      )}

      {/* Duplicate Button */}
      {sourceGroupId && targetGroupId && preview && selectedProducts.size > 0 && (
        <div className="duplicate-action">
          <button 
            className="btn-duplicate" 
            onClick={promptPassword} 
            disabled={duplicating}
          >
            {duplicating ? (
              <><FontAwesomeIcon icon={faSpinner} spin /> Duplication en cours...</>
            ) : (
              <><FontAwesomeIcon icon={faCopy} /> Dupliquer {selectedProducts.size} produit(s) vers "{targetGroup?.nom}"</>
            )}
          </button>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className={`result-section ${result.success ? 'success' : 'error'}`}>
          <div className="result-header">
            <FontAwesomeIcon icon={result.success ? faCheckCircle : faExclamationTriangle} />
            <h3>{result.message}</h3>
          </div>
          {result.results && (
            <div className="result-details">
              <div className="result-stat"><FontAwesomeIcon icon={faBox} /> {result.results.products} produit(s) copié(s)</div>
              <div className="result-stat"><FontAwesomeIcon icon={faTags} /> {result.results.categories} catégorie(s) créée(s)</div>
              <div className="result-stat"><FontAwesomeIcon icon={faTruck} /> {result.results.suppliers} fournisseur(s) créé(s)</div>
              <div className="result-stat"><FontAwesomeIcon icon={faImage} /> {result.results.images} image(s) copiée(s)</div>
              {result.results.errors?.length > 0 && (
                <div className="result-errors">
                  <h4><FontAwesomeIcon icon={faExclamationTriangle} /> {result.results.errors.length} erreur(s):</h4>
                  <ul>
                    {result.results.errors.map((err, i) => <li key={i}>{err}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Password Confirmation Modal */}
      {showPasswordModal && (
        <div className="password-modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="password-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirmation de sécurité</h3>
              <p>Entrez votre mot de passe administrateur pour confirmer la duplication</p>
            </div>
            <div className="modal-body">
              <div className="modal-summary">
                <span><strong>{selectedProducts.size}</strong> produit(s) de <strong>{sourceGroup?.nom}</strong></span>
                <FontAwesomeIcon icon={faArrowRight} />
                <span><strong>{targetGroup?.nom}</strong></span>
              </div>
              <div className="password-field">
                <label>Mot de passe</label>
                <div className="password-input-wrapper">
                  <input 
                    type={showPassword ? 'text' : 'password'} 
                    value={adminPassword}
                    onChange={(e) => { setAdminPassword(e.target.value); setPasswordError(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleDuplicate(); }}
                    placeholder="Votre mot de passe admin..."
                    autoFocus
                  />
                  <button 
                    type="button" 
                    className="password-toggle" 
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
                {passwordError && <span className="password-error">{passwordError}</span>}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => { setShowPasswordModal(false); setAdminPassword(''); }}>
                Annuler
              </button>
              <button 
                className="btn-confirm" 
                onClick={handleDuplicate}
                disabled={!adminPassword || duplicating}
              >
                {duplicating ? (
                  <><FontAwesomeIcon icon={faSpinner} spin /> Duplication...</>
                ) : (
                  <><FontAwesomeIcon icon={faCopy} /> Confirmer la duplication</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockDuplication;
